import React, { useMemo, useCallback } from 'react';
import { Box } from '@tanium/react-box';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, FormikConfig } from 'formik';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { IdpSetupConfiguration } from './IdpSetupConfiguration';
import { IdpFormValidationSchema } from './IdpFormValidationSchema';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { DEFAULT_EMAIL_ATTRIBUTE_NAME } from '../../utilities/constants';

interface IdpSetupModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  initialConfig?: ConfigureIdpRequest;
}

export const IdpSetupModal: React.VFC<IdpSetupModalProps> = ({ initialConfig, isOpen, onRequestClose }) => {
  const { t } = useTranslation();
  const { idpConfigs } = useIdentityProviderConfigs();

  const generateUniqueIdpName = useCallback(() => {
    let idpName = crypto.randomUUID().split('-').join('').substring(0, 5);
    // if the idp name already exists, generate a new one
    /* istanbul ignore if */
    if (idpConfigs.find((idp) => idp.name === idpName)) {
      idpName = generateUniqueIdpName();
    }
    return idpName;
  }, [idpConfigs]);

  const modalHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box mt={-2} mb={-2}>
        <h4>{initialConfig ? t('Update Identity Provider Settings') : t('Add Identity Provider')}</h4>
      </Box>
      <IconButton icon={CloseIcon} height={16} width={16} label={t('Close')} onClick={onRequestClose} />
    </Stack>
  );

  const initialValues = useMemo(
    () =>
      ({
        id: initialConfig?.id,
        name: initialConfig?.name || generateUniqueIdpName(),
        displayName: initialConfig?.displayName ?? '',
        emailAttributeName: initialConfig?.emailAttributeName ?? DEFAULT_EMAIL_ATTRIBUTE_NAME,
        samlMetadataUrl: initialConfig?.samlMetadataUrl ?? '',
        samlMetadataFile: initialConfig?.samlMetadataFile ?? '',
        enableTaniumConsoleAuth: initialConfig?.enableTaniumConsoleAuth ?? false,
        enableAccountPortalAuth: initialConfig?.enableAccountPortalAuth ?? true,
        enableIdpSignout: initialConfig?.enableIdpSignout ?? false,
        domainNames: initialConfig?.domainNames ?? [],
        roleMappers: initialConfig?.roleMappers,
      } as ConfigureIdpRequest),
    [initialConfig, generateUniqueIdpName]
  );

  const formikProps = {
    initialValues,
    validationSchema: IdpFormValidationSchema(t),
  } as FormikConfig<ConfigureIdpRequest>;

  return (
    <Modal isOpen={isOpen} header={modalHeader} size="lg" onRequestClose={onRequestClose}>
      <Box>
        <Formik {...formikProps}>
          {(formData: FormikProps<ConfigureIdpRequest>): React.ReactElement => (
            <IdpSetupConfiguration formData={formData} onClose={onRequestClose} />
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
